import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Autocomplete,
  Badge,
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { TreeView, TreeItem } from "@mui/x-tree-view";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SearchIcon from "@mui/icons-material/Search";
import FolderIcon from "@mui/icons-material/Folder";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import DescriptionIcon from "@mui/icons-material/Description";
import ArticleIcon from "@mui/icons-material/Article";
import CalculateIcon from "@mui/icons-material/Calculate";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import AddIcon from "@mui/icons-material/Add";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import GavelIcon from "@mui/icons-material/Gavel";
import ReceiptIcon from "@mui/icons-material/Receipt";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import AssignmentReturnIcon from "@mui/icons-material/AssignmentReturn";
import SendIcon from "@mui/icons-material/Send";
import PlantModuleNav from "../../PlantModuleNav";
import { PLANT_MODULE_GROUPS } from "../../../constants/plantModules";
import { buildEstimationHref } from "./estimationLinks";
import paths from "../../../api/paths.api";
import {
  wbsLabel,
  isPvWbs,
  extractProjectEstimationRevisions,
  documentAbbreviations,
  WBS_GROUPS,
  wbsGroupNodeId,
  treeGroupKey,
  isSoftwareWbs,
  softwareGroupKey,
  softwareGroupLabel,
  softwareGroupNodeId,
} from "./useEmsProjectsTreeData";
import {
  PROJECT_TYPES,
  disciplineDefaultType,
  projectTypeLabel,
  projectTypeDiscipline,
  pipingWbsTabs,
  isSingletonProjectType,
} from "../../../constants/projectTypes";

const DCS_DOC_TYPES = ["Engineering", "MRQ", "RFI", "Client"];

// Estimation page tab (Tank_Estimation's equipment tabs) that opens a
// Structure / Instrumentation / Piping WBS's Estimation revisions.
const estimationTabLabel = (wbsType) => {
  const discipline = projectTypeDiscipline(wbsType);
  if (discipline === "struc") return "Structure";
  if (discipline === "ins") return "Instrumentation";
  return "Piping";
};

// Next free revision number for a loaded list of revision-bearing entries,
// matching how the modules' own "+ Add New Revision" numbers one. null while
// the list hasn't loaded, so callers can tell "no revisions yet" (-> "0")
// apart from "don't know yet".
const nextRevisionNumber = (entries) => {
  if (!Array.isArray(entries)) return null;
  const numbers = entries
    .map((entry) => Number(entry?.revision))
    .filter((value) => !Number.isNaN(value));
  return String(numbers.length ? Math.max(...numbers) + 1 : 0);
};

const plantNodeId = (id) => `plant-${id}`;
const wbsNodeId = (id) => `wbs-${id}`;
const inputDataNodeId = (projectId, revision) =>
  `input-${projectId}-${revision}`;
const estimationNodeId = (projectId, revision) =>
  `estimation-${projectId}-${revision}`;
const specNodeId = (projectId) => `spec-${projectId}`;

const procurementNodeId = (plantId, categoryKey) =>
  `proc-${plantId}-${categoryKey}`;
const procurementItemNodeId = (plantId, categoryKey, itemId) =>
  `procitem-${plantId}-${categoryKey}-${itemId}`;

const moduleItemNodeId = (plantId, itemKey) => `modtile-${plantId}-${itemKey}`;

// EMS (Doc. Reviewer), CMS (Fabrication/ITP) and 4D Schedule (one per
// module that has one) tiles, reusing the same catalogue PlantModuleNav's
// "Apps" menu is built from (constants/plantModules.js) so this tree and
// that menu can't drift apart - each item's `path`/`hash` is exactly what
// buildModuleHref there uses too.
const EMS_MODULE_GROUP = PLANT_MODULE_GROUPS.find((g) => g.label === "EMS");
const CMS_MODULE_GROUP = PLANT_MODULE_GROUPS.find((g) => g.label === "CMS");
const SCHEDULE_MODULE_GROUP = PLANT_MODULE_GROUPS.find(
  (g) => g.label === "4D Schedule",
);

// Three quick-link tiles ahead of the live WBS tree below them: unlike that
// tree (which drills into a specific WBS's documents), these jump straight
// to a plant-wide register page. `path: ""` means the plant root itself
// (see the "module" case in handleNodeSelect).
const REGISTER_TILES = [
  {
    key: "wbs-register",
    label: "WBS Register",
    path: "",
    icon: AccountTreeIcon,
  },
  {
    key: "doc-register",
    label: "Doc Register",
    path: "document-register",
    icon: LibraryBooksIcon,
  },
  {
    key: "doc-transmittal-register",
    label: "Doc Transmittal Register",
    path: "dcs",
    hash: { "dcs-tab": "Doc. Transmittal" },
    icon: SendIcon,
  },
];

const PROCUREMENT_CATEGORIES = [
  {
    key: "mrq",
    label: "MRQ",
    icon: ShoppingCartIcon,
    pathKey: "plantMRQ",
    apiKey: "mrqs",
  },
  {
    key: "vendorBidEvaluation",
    label: "Vendor Bid Evaluation",
    icon: GavelIcon,
    pathKey: "plantVendorBidEvaluation",
    apiKey: "bids",
  },
  {
    key: "po",
    label: "PO",
    icon: ReceiptIcon,
    pathKey: "plantPO",
    apiKey: "pos",
  },
  {
    key: "mrv",
    label: "MRV",
    icon: LocalShippingIcon,
    pathKey: "plantMRV",
    apiKey: "mrvs",
  },
  {
    key: "srv",
    label: "SRV",
    icon: AssignmentReturnIcon,
    pathKey: "plantSRV",
    apiKey: "srvs",
  },
];

/**
 * Presentational Plant -> WBS -> Documents/Input Data/Estimation/2D
 * Drawings tree, driven by useEmsProjectsTreeData(). Shared by the global
 * AppSidebar's ProjectsTreeSection and the full-page EmsExplorer so both
 * look and behave identically for search/expand/load.
 *
 * Clicking a Plant/WBS/Document node calls `onSelect({kind, plant, wbs?,
 * doc?})` so each consumer decides what happens (navigate away, or show
 * inline) — but PV Input Data/Estimation/2D Drawing leaves always confirm
 * then navigate away here, since both consumers want that identical
 * behavior (those are full editable forms living on other pages, and
 * leaving this page loses whatever isn't saved there).
 *
 * `collapsible` turns the "Projects" heading into a toggle and starts the
 * whole list hidden, and `focusSelectedPlant` narrows the list to just the
 * picked plant afterwards, with the header breadcrumb (Projects / plant)
 * to step back out - everything inside that plant just expands in place,
 * as it does everywhere else. Both are for the narrow side-panel drawer,
 * where showing every plant up front is more noise than help. The
 * full-page explorer and the compare picker leave them off.
 */
export default function EmsProjectsTreeView({
  data,
  activePlantId,
  onSelect,
  pickerMode = false,
  showLegend = true,
  collapsible = false,
  defaultListOpen,
  focusSelectedPlant = false,
}) {
  const navigate = useNavigate();
  const {
    plants,
    plantsLoading,
    search,
    handleSearchChange,
    isSearchLoading,
    query,
    wbsByPlant,
    docsByWbs,
    pvInputsByWbs,
    mcsTreeByPlant,
    expanded,
    setExpanded,
    handleNodeToggle,
    isPlantVisible,
    isWbsVisible,
    createWbs,
    createDocument,
    loadMcsTreeForPlant,
  } = data;

  // Pending Input Data/Estimation/Drawing node awaiting user confirmation
  // before navigating away from this page.
  const [confirmTarget, setConfirmTarget] = useState(null);

  // Collapsible mode only: the list stays hidden behind the "Projects"
  // heading until it's clicked - `defaultListOpen` overrides that initial
  // state for a consumer (e.g. AppSidebar) that wants the breadcrumb-style
  // "Projects" toggle button without starting collapsed.
  const [listOpen, setListOpen] = useState(defaultListOpen ?? !collapsible);
  // focusSelectedPlant mode only: id of the plant the list has narrowed
  // down to (null = showing every plant).
  const [focusedPlantId, setFocusedPlantId] = useState(null);

  // Plant a "+ Add WBS" click is pending for (null = dialog closed).
  const [addWbsTarget, setAddWbsTarget] = useState(null);
  // `description` is UI-only - the create WBS endpoint has no field for it
  // (see createWbs in useEmsProjectsTreeData.js), so it's never sent.
  const [wbsForm, setWbsForm] = useState({
    projectName: "",
    description: "",
    type: "",
    link: "",
  });
  // Type the dialog was opened pinned to ("" = free choice). Set when the add
  // button of a discipline folder was used - see openAddWbs.
  const [lockedWbsType, setLockedWbsType] = useState("");
  const [savingWbs, setSavingWbs] = useState(false);
  const [wbsError, setWbsError] = useState("");

  // { plant, wbs } a "+ Add Document" click is pending for (null = closed).
  const [addDocTarget, setAddDocTarget] = useState(null);
  const [docForm, setDocForm] = useState({
    abbrEntry: null,
    type: "Engineering",
    revision: "",
    description: "",
    numberOfSheets: "",
  });
  const [savingDoc, setSavingDoc] = useState(false);
  const [docError, setDocError] = useState("");

  // `presetType` comes from the "+" on a discipline folder: that button adds
  // another WBS of the folder's own type, so the type is filled in and locked
  // rather than asked for again. The plant-level button passes none and the
  // picker stays open.
  const openAddWbs = (plant, presetType = "") => {
    setAddWbsTarget(plant);
    setWbsForm({
      projectName: "",
      description: "",
      type: presetType,
      link: "",
    });
    setLockedWbsType(presetType);
    setWbsError("");
  };

  // A one-per-plant type (Spec) the plant already holds. Checked against every
  // WBS in the plant, not just those the current search leaves visible. The
  // create endpoint doesn't enforce this, so the dialog is what does.
  const isSingletonTypeTaken = (plant, type) => {
    if (!isSingletonProjectType(type)) return false;
    const plantWbs = wbsByPlant[plant?.id];
    const wanted = String(type).trim().toLowerCase();
    return (
      Array.isArray(plantWbs) &&
      plantWbs.some(
        (wbs) =>
          String(wbs?.type ?? "")
            .trim()
            .toLowerCase() === wanted,
      )
    );
  };

  const handleCreateWbs = async () => {
    const projectName = wbsForm.projectName.trim();
    if (!projectName) {
      setWbsError("WBS code is required");
      return;
    }
    if (!wbsForm.type) {
      setWbsError("Type is required");
      return;
    }
    if (isSingletonTypeTaken(addWbsTarget, wbsForm.type)) {
      setWbsError(
        `This plant already has a ${projectTypeLabel(wbsForm.type)} WBS - only one is allowed.`,
      );
      return;
    }
    setSavingWbs(true);
    setWbsError("");
    try {
      await createWbs(addWbsTarget.id, {
        projectName,
        type: wbsForm.type,
        link: wbsForm.link.trim(),
      });
      setAddWbsTarget(null);
    } catch (err) {
      const message = err?.response?.data?.message || "Failed to create WBS";
      setWbsError(Array.isArray(message) ? message.join(", ") : message);
    } finally {
      setSavingWbs(false);
    }
  };

  const openAddDoc = (plant, wbs) => {
    setAddDocTarget({ plant, wbs });
    setDocForm({
      abbrEntry: null,
      type: "Engineering",
      revision: "",
      description: "",
      numberOfSheets: "",
    });
    setDocError("");
  };

  const handleCreateDoc = async () => {
    if (!docForm.abbrEntry) {
      setDocError("Pick a document type");
      return;
    }
    setSavingDoc(true);
    setDocError("");
    try {
      await createDocument(addDocTarget.plant, addDocTarget.wbs, {
        abbreviation: docForm.abbrEntry.abbreviation,
        type: docForm.type,
        revision: docForm.revision.trim(),
        description:
          docForm.description.trim() || docForm.abbrEntry.description,
        numberOfSheets: docForm.numberOfSheets,
      });
      setAddDocTarget(null);
    } catch (err) {
      const message =
        err?.response?.data?.message || "Failed to create document";
      setDocError(Array.isArray(message) ? message.join(", ") : message);
    } finally {
      setSavingDoc(false);
    }
  };

  // Rebuilt on every render while walking the tree, so node clicks can
  // resolve their full context (plant/wbs/doc) without re-parsing ids.
  const nodeTargetRef = React.useRef({});

  // A search looks across every project, so starting one drops the focus
  // (see handleSearchInput) - otherwise matches outside the picked plant
  // would silently never show up. The focus is not second-guessed here,
  // though: picking a result sets it and clears the search in the same
  // click, and the query trails that by a debounce.
  const focusedPlant =
    focusSelectedPlant && focusedPlantId
      ? plants.find((plant) => plant.id === focusedPlantId)
      : null;

  // Typing steps back out of any drill-down - results span every project, and
  // a focus left in place would hide most of them.
  const handleSearchInput = (value) => {
    setFocusedPlantId(null);
    // Emptying the box puts the full list back, so it comes back collapsed:
    // the plants and discipline folders a search opened to show its matches
    // have no reason to stay open once there's nothing to match.
    if (!value.trim()) setExpanded([]);
    handleSearchChange(value);
  };

  const handleNodeSelect = (event, nodeId) => {
    const target = nodeTargetRef.current[nodeId];
    if (!target) return;

    if (focusSelectedPlant && target.kind === "plant") {
      if (search) handleSearchChange("");
      setFocusedPlantId(target.plant.id);
      const id = plantNodeId(target.plant.id);
      if (!expanded.includes(id)) {
        handleNodeToggle(event, [...expanded, id]);
      } else {
        loadMcsTreeForPlant(target.plant.id);
      }
    }

    if (target.kind === "procurement") {
      const category = PROCUREMENT_CATEGORIES.find(
        (c) => c.key === target.procurementType,
      );
      if (category) {
        navigate(paths[category.pathKey].replace(":plantId", target.plantId));
      }
      return;
    }

    if (target.kind === "module") {
      const hash = target.item.hash
        ? `#${new URLSearchParams(target.item.hash).toString()}`
        : "";
      const base = target.item.path
        ? `/plant/${target.plantId}/${target.item.path}`
        : `/plant/${target.plantId}`;
      // A specific sub-tab was picked (not just the module's flat tile), so
      // land on just that tab - see useStandaloneView.
      const query = target.item.hash ? "?standalone=1" : "";
      navigate(`${base}${query}${hash}`);
      return;
    }

    if (!pickerMode && (target.kind === "pv" || target.kind === "drawing")) {
      setConfirmTarget(target);
      return;
    }

    onSelect?.(target);
  };

  const handleConfirmOpen = () => {
    if (!confirmTarget) return;

    if (confirmTarget.kind === "drawing") {
      // MCS3's DrawingBomContext (BOMTab/DrawingBom/DrawingBomContext/index.jsx)
      // already watches the "drawing-id" hash key on mount and fetches +
      // adds that drawing to the working set shown in the BOM Dashboard
      // table - no query params needed, just land on that tab with the hash set.
      const mcsHash = new URLSearchParams({
        "mcs-tab": "Bulk Materials",
        "bom-tab": "BOM Dashboard",
        "drawing-id": confirmTarget.drawingId,
      });
      navigate(`/plant/${confirmTarget.plantId}/mcs#${mcsHash.toString()}`);
      setConfirmTarget(null);
      return;
    }

    // Structure numbers its own new revision once its data is loaded, so the
    // link carries no revision for that case - everything else names one.
    // `wbsType` is which half of Piping to open: projectId above can be a
    // different WBS (a Spec WBS links to the row its data lives on), so the
    // module cannot infer this from the project it loads - the tree has to
    // name it.
    navigate(
      buildEstimationHref({
        plantId: confirmTarget.plantId,
        projectId: confirmTarget.projectId,
        projectName: confirmTarget.projectName,
        revision: confirmTarget.revision,
        topTabLabel: confirmTarget.topTabLabel,
        subTabLabel: confirmTarget.subTabLabel,
        wbsType: confirmTarget.wbsType,
        newRevision: confirmTarget.kind === "new-revision",
      }),
    );
    setConfirmTarget(null);
  };

  const handleConfirmCancel = () => setConfirmTarget(null);

  // One PV revision: the Input Data document UID is the revision itself, so
  // that revision's Estimation hangs underneath it rather than in a separate
  // top-level folder - PV's Estimation is always of a specific input revision.
  const renderPvInputNode = (plant, wbs, entry) => {
    const id = inputDataNodeId(wbs.id, entry.revision);
    nodeTargetRef.current[id] = {
      kind: "pv",
      plantId: plant.id,
      projectId: wbs.id,
      projectName: wbsLabel(wbs),
      revision: entry.revision,
      subTabLabel: "Input Data",
    };
    return (
      <TreeItem
        key={id}
        nodeId={id}
        className="branch-accent"
        icon={<ArticleIcon fontSize="inherit" sx={{ color: "warning.main" }} />}
        label={
          <Typography
            variant="caption"
            title={entry.documentUID}
            sx={{ whiteSpace: "normal", wordBreak: "break-word" }}
          >
            {entry.documentUID}
          </Typography>
        }
      >
        {entry.estimationDocumentUID
          ? renderEstimationNode(plant, wbs, entry)
          : null}
      </TreeItem>
    );
  };

  const renderEstimationNode = (plant, wbs, entry) => {
    const id = estimationNodeId(wbs.id, entry.revision);
    nodeTargetRef.current[id] = {
      kind: "pv",
      plantId: plant.id,
      projectId: wbs.id,
      projectName: wbsLabel(wbs),
      revision: entry.revision,
      subTabLabel: "Estimation",
    };
    return (
      <TreeItem
        key={id}
        nodeId={id}
        icon={
          <CalculateIcon fontSize="inherit" sx={{ color: "warning.main" }} />
        }
        label={
          // Named for what it is rather than by its document UID: it sits
          // under the input revision it belongs to, which already carries the
          // UID. The full estimation UID stays in the tooltip.
          <Typography
            variant="caption"
            fontWeight={600}
            color="text.secondary"
            title={entry.estimationDocumentUID}
            sx={{ whiteSpace: "normal", wordBreak: "break-word" }}
          >
            Estimation
          </Typography>
        }
      />
    );
  };

  // Structure/Piping's own Estimation - a project revision's estimationTable
  // cost summary, not PV's per-document-UID entries, so there's no natural
  // document identifier to label these with beyond the revision itself.
  const renderProjectEstimationNode = (plant, wbs, entry) => {
    const id = estimationNodeId(wbs.id, entry.revision);
    const topTabLabel = estimationTabLabel(wbs.type);
    nodeTargetRef.current[id] = {
      kind: "pv",
      plantId: plant.id,
      projectId: wbs.id,
      projectName: wbsLabel(wbs),
      revision: entry.revision,
      wbsType: wbs.type,
      topTabLabel,
    };
    return (
      <TreeItem
        key={id}
        nodeId={id}
        icon={
          <CalculateIcon fontSize="inherit" sx={{ color: "warning.main" }} />
        }
        label={
          <Typography variant="caption" sx={{ whiteSpace: "normal" }}>
            {`Rev ${entry.revision}`}
          </Typography>
        }
      />
    );
  };

  // Piping's Spec, now a WBS of its own rather than a tab on the Piping WBS.
  // A single leaf, not a folder of revisions: `selectedSpecs` is one flat list
  // on the project, never keyed by revision the way estimationTable is.
  //
  // The Spec WBS is its own record holding its own specs, so it links to its
  // own id - `wbsType` is what tells the module to open Spec, not Estimation.
  const renderSpecNode = (plant, wbs) => {
    const id = specNodeId(wbs.id);
    nodeTargetRef.current[id] = {
      kind: "pv",
      plantId: plant.id,
      projectId: wbs.id,
      projectName: wbsLabel(wbs),
      wbsType: wbs.type,
      topTabLabel: "Piping",
      // Names the tab in the confirm dialog only. Deliberately not
      // subTabLabel, which would also add a "pv-modeling" hash meant for PV.
      openLabel: "Spec",
    };
    return (
      <TreeItem
        key={id}
        nodeId={id}
        icon={
          <LibraryBooksIcon fontSize="inherit" sx={{ color: "warning.main" }} />
        }
        label={
          <Typography variant="caption" sx={{ whiteSpace: "normal" }}>
            Spec
          </Typography>
        }
      />
    );
  };

  const renderProcurementItemNode = (plant, category, item) => {
    const rowId = item?.id ?? item?.mrqId ?? item?.mrqNumber ?? item?.poNumber;
    const id = procurementItemNodeId(plant.id, category.key, rowId);
    nodeTargetRef.current[id] = {
      kind: "procurement",
      plantId: plant.id,
      procurementType: category.key,
      itemId: item.id,
    };

    const Icon = category.icon;
    // PO rows show the PO number + revision; bid-evaluation rows are one per
    // MRQ that has vendor bids, so they show that MRQ's number + revision.
    let number;
    let revision;
    if (category.key === "po") {
      number =
        item.poNumber ||
        item.number ||
        item.code ||
        item.name ||
        `#${rowId}`;
      revision =
        item.revision ??
        item.rev ??
        item.poRevision ??
        item.revisionNumber ??
        "";
    } else if (category.key === "vendorBidEvaluation") {
      const sourceMrq = item.mrq && typeof item.mrq === "object" ? item.mrq : null;
      number =
        item.mrqNumber ||
        sourceMrq?.mrqNumber ||
        item.number ||
        item.code ||
        item.name ||
        `#${rowId}`;
      revision =
        item.rev ??
        item.revision ??
        item.mrqRevision ??
        sourceMrq?.rev ??
        sourceMrq?.revision ??
        "";
    } else {
      number =
        item.mrqNumber ||
        item.poNumber ||
        item.mrvNumber ||
        item.srvNumber ||
        item.number ||
        item.code ||
        item.name ||
        `#${rowId}`;
      revision =
        item.mrqRevision ??
        item.poRevision ??
        item.mrvRevision ??
        item.srvRevision ??
        item.revision ??
        item.rev ??
        item.revisionNumber ??
        "";
    }
    const displayText =
      revision !== "" && revision != null
        ? `${number} (Rev ${revision})`
        : String(number);

    return (
      <TreeItem
        key={id}
        nodeId={id}
        icon={<Icon fontSize="inherit" sx={{ color: "info.main" }} />}
        label={
          <Typography
            variant="caption"
            title={displayText}
            sx={{ whiteSpace: "normal", wordBreak: "break-word" }}
          >
            {displayText}
          </Typography>
        }
      />
    );
  };

  // One EMS/CMS tile leaf - unlike Procurement's categories these are plain
  // destinations (a module tab), not registries of items, so there is no
  // count/loading state to show, just an icon and a click-through.
  const renderModuleItemNode = (plant, item) => {
    const id = moduleItemNodeId(plant.id, item.key);
    nodeTargetRef.current[id] = { kind: "module", plantId: plant.id, item };

    const Icon = item.icon;
    return (
      <TreeItem
        key={id}
        nodeId={id}
        icon={<Icon fontSize="inherit" sx={{ color: "info.main" }} />}
        label={
          <Typography variant="body2" noWrap>
            {item.label}
          </Typography>
        }
      />
    );
  };

  const renderProcurementNode = (plant, category) => {
    const id = procurementNodeId(plant.id, category.key);
    const plantData = mcsTreeByPlant[plant.id];
    const rawItems =
      plantData && plantData !== "loading"
        ? plantData[category.apiKey]
        : undefined;
    // `plantData.pos` / `plantData.bids` are normalized from the plant's MRQs
    // (see normalizeMcsTreeData), but fall back to deriving them here so rows
    // still show even if the payload only embeds POs/bids on each MRQ.
    let items = Array.isArray(rawItems) ? rawItems : [];
    if (items.length === 0 && plantData && plantData !== "loading") {
      const mrqs = Array.isArray(plantData.mrqs) ? plantData.mrqs : [];
      if (category.key === "po") {
        items = mrqs.flatMap((mrq) =>
          Array.isArray(mrq?.pos)
            ? mrq.pos
            : Array.isArray(mrq?.POs)
              ? mrq.POs
              : Array.isArray(mrq?.po)
                ? mrq.po
                : [],
        );
      } else if (category.key === "vendorBidEvaluation") {
        items = mrqs
          .filter((mrq) => {
            const bids = Array.isArray(mrq?.bids)
              ? mrq.bids
              : Array.isArray(mrq?.bid)
                ? mrq.bid
                : Array.isArray(mrq?.Bids)
                  ? mrq.Bids
                  : null;
            return Array.isArray(bids) && bids.length > 0;
          })
          .map((mrq) => ({
            id: mrq?.id,
            mrqId: mrq?.id,
            mrqNumber: mrq?.mrqNumber,
            rev: mrq?.rev,
            revision: mrq?.revision,
            mrq,
          }));
      }
    }
    const count = items.length;
    const Icon = category.icon;

    return (
      <TreeItem
        key={id}
        nodeId={id}
        icon={<Icon fontSize="inherit" sx={{ color: "info.main" }} />}
        label={
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              minWidth: 0,
            }}
          >
            <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>
              {category.label}
            </Typography>
            {plantData && plantData !== "loading" && (
              <Typography variant="caption" color="text.disabled">
                ({count})
              </Typography>
            )}
          </Box>
        }
      >
        {plantData === "loading" ? (
          <TreeItem
            key={`${id}-loading`}
            nodeId={`${id}-loading`}
            label={
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5 }}
              >
                <CircularProgress size={12} />
                <Typography variant="caption" color="text.secondary">
                  Loading…
                </Typography>
              </Box>
            }
          />
        ) : items.length > 0 ? (
          items.map((item) => renderProcurementItemNode(plant, category, item))
        ) : (
          <TreeItem
            key={`${id}-empty`}
            nodeId={`${id}-empty`}
            label={
              <Typography variant="caption" color="text.secondary">
                No items
              </Typography>
            }
          />
        )}
      </TreeItem>
    );
  };

  // A labeled folder node for one branch of a WBS (e.g. Estimation) so the tree reads as Plant → WBS → category →
  // item, instead of dumping every item type into one flat list. `state` is
  // "loading", an array (possibly empty), or undefined (not fetched yet).
  const renderCategoryFolder = (
    categoryId,
    label,
    icon,
    state,
    loadingLabel,
    emptyLabel,
    items,
    { variant = "accent", action = null } = {},
  ) => {
    let children;
    if (state === "loading") {
      children = [
        <TreeItem
          key={`${categoryId}-loading`}
          nodeId={`${categoryId}-loading`}
          label={
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5 }}
            >
              <CircularProgress size={12} />
              <Typography variant="caption" color="text.secondary">
                {loadingLabel}
              </Typography>
            </Box>
          }
        />,
      ];
    } else if (Array.isArray(state)) {
      children =
        items.length > 0
          ? items
          : [
              <TreeItem
                key={`${categoryId}-empty`}
                nodeId={`${categoryId}-empty`}
                label={
                  <Typography variant="caption" color="text.secondary">
                    {emptyLabel}
                  </Typography>
                }
              />,
            ];
    } else {
      // Not loaded yet — placeholder keeps the expand arrow visible.
      children = [
        <TreeItem
          key={`${categoryId}-placeholder`}
          nodeId={`${categoryId}-placeholder`}
          label=""
        />,
      ];
    }

    return (
      <TreeItem
        key={categoryId}
        nodeId={categoryId}
        icon={icon}
        className={`branch-${variant}`}
        label={
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              minWidth: 0,
            }}
          >
            <Typography
              variant="caption"
              fontWeight={600}
              color="text.secondary"
              noWrap
              sx={{ flex: 1, minWidth: 0 }}
            >
              {label}
            </Typography>
            {action}
          </Box>
        }
      >
        {children}
      </TreeItem>
    );
  };

  // "+ Add revision" — on the WBS row for PV, whose revisions hang directly
  // off it, and on the Estimation folder elsewhere. The tree's stand-in
  // for the "+ Add New Revision" option those modules used to offer in their
  // header Rev. dropdown. Like that option it only branches a revision; it is
  // saved when the user saves on the module page it opens.
  const renderAddRevisionAction = (
    plant,
    wbs,
    { revision, subTabLabel, topTabLabel, wbsType },
  ) => {
    if (pickerMode) return null;

    // PV needs the revision number up front (its page takes it from the URL),
    // and that can only be derived once the revision list has loaded.
    const disabled = revision === null;

    return (
      <IconButton
        size="small"
        disabled={disabled}
        title={
          disabled
            ? "Expand this WBS to load its revisions first"
            : "Add a new revision"
        }
        onClick={(event) => {
          event.stopPropagation();
          setConfirmTarget({
            kind: "new-revision",
            plantId: plant.id,
            projectId: wbs.id,
            projectName: wbsLabel(wbs),
            revision,
            subTabLabel,
            topTabLabel,
            wbsType,
          });
        }}
        sx={{ p: 0.25 }}
      >
        <AddIcon fontSize="small" sx={{ color: "warning.main" }} />
      </IconButton>
    );
  };

  const renderWbsNode = (plant, wbs) => {
    const id = wbsNodeId(wbs.id);
    nodeTargetRef.current[id] = { kind: "wbs", plant, wbs };
    // Empty for anything that is not a Piping WBS - PV and Structural pick
    // their folders off isPvWbs and the discipline, as before.
    const pipingTabs = pipingWbsTabs(wbs.type);
    const pvInputs = isPvWbs(wbs) ? pvInputsByWbs[wbs.id] : undefined;
    // Only for the document-count badge on the WBS row: the Documents and
    // 2D Drawings folders were taken out of the tree, which now shows just a
    // WBS's working data (Spec / Input Data / Estimation).
    const docs = docsByWbs[wbs.id];

    const categories = [];

    if (isPvWbs(wbs)) {
      // PV's input-data revisions hang straight off the WBS - the folder that
      // used to wrap them only ever held this one list, and its "+" now sits
      // on the WBS row itself (see the label below).
      if (pvInputs === "loading") {
        categories.push(
          <TreeItem
            key={`${id}-input-loading`}
            nodeId={`${id}-input-loading`}
            label={
              <Typography variant="caption" color="text.secondary">
                Loading input data…
              </Typography>
            }
          />,
        );
      } else if (Array.isArray(pvInputs) && pvInputs.length > 0) {
        pvInputs.forEach((entry) =>
          categories.push(renderPvInputNode(plant, wbs, entry)),
        );
      } else if (Array.isArray(pvInputs)) {
        categories.push(
          <TreeItem
            key={`${id}-input-empty`}
            nodeId={`${id}-input-empty`}
            label={
              <Typography variant="caption" color="text.secondary">
                No input data revisions
              </Typography>
            }
          />,
        );
      } else {
        // Not loaded yet - the revisions are only fetched when this WBS is
        // expanded (see handleNodeToggle), so without a child here the WBS
        // would have no expand arrow and could never load them.
        categories.push(
          <TreeItem
            key={`${id}-placeholder`}
            nodeId={`${id}-placeholder`}
            label=""
          />,
        );
      }
    } else {
      // Piping's Spec and Estimation are separate WBS now, so a WBS carries
      // only the parts its own type opens: Spec for a "spec" WBS, Estimation
      // for a "PIE" one, and both for a legacy "pip" WBS, which still holds
      // the two tabs together. Structural never had a Spec half and keeps its
      // Estimation folder unchanged.
      if (pipingTabs.includes("spec")) {
        // A plain node rather than a folder: there is only ever the one Spec
        // to open, so a folder around a single child would read "Spec > Spec"
        // and cost a click.
        categories.push(renderSpecNode(plant, wbs));
      }

      if (
        pipingTabs.includes("estimation") ||
        projectTypeDiscipline(wbs.type) === "struc" ||
        // Instrumentation's Estimation works like Piping's (its own module,
        // Ams/Instrumentation), so it gets the same folder.
        projectTypeDiscipline(wbs.type) === "ins"
      ) {
        // Already embedded in wbs.revisions from the same WBS-list fetch that
        // populated this node - no separate loading state needed, unlike PV's
        // Input Data/Estimation which come from their own /PV/all call.
        const estimationRevisions = extractProjectEstimationRevisions(wbs);
        categories.push(
          renderCategoryFolder(
            `${id}-cat-estimation`,
            "Estimation",
            <CalculateIcon fontSize="inherit" sx={{ color: "warning.main" }} />,
            estimationRevisions,
            "Loading estimation…",
            "No estimation revisions",
            estimationRevisions.map((entry) =>
              renderProjectEstimationNode(plant, wbs, entry),
            ),
            {
              // `revision: undefined` lets the module page number the new one
              // off the estimation data it just loaded, rather than the tree
              // guessing from the WBS list.
              action: renderAddRevisionAction(plant, wbs, {
                revision: undefined,
                topTabLabel: estimationTabLabel(wbs.type),
                wbsType: wbs.type,
              }),
            },
          ),
        );
      }
    }

    return (
      <TreeItem
        key={id}
        nodeId={id}
        icon={
          <AccountTreeIcon fontSize="inherit" sx={{ color: "primary.main" }} />
        }
        label={
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              minWidth: 0,
            }}
          >
            <Typography
              variant="body2"
              noWrap
              title={wbsLabel(wbs)}
              sx={{ flex: 1, minWidth: 0 }}
            >
              {wbsLabel(wbs)}
            </Typography>
            {isPvWbs(wbs) &&
              // PV's Rev. dropdown lives on Input Data > Design, so that's
              // where a new revision opens.
              renderAddRevisionAction(plant, wbs, {
                revision: nextRevisionNumber(pvInputs),
                subTabLabel: "Input Data",
              })}
            {!pickerMode && (
              <IconButton
                size="small"
                title={
                  Array.isArray(docs)
                    ? `${docs.length} document${docs.length === 1 ? "" : "s"} in the register — click to add another`
                    : "Add a document to this WBS's register"
                }
                onClick={(event) => {
                  event.stopPropagation();
                  openAddDoc(plant, wbs);
                }}
                sx={{ p: 0.4 }}
              >
                <Badge
                  badgeContent={Array.isArray(docs) ? docs.length : undefined}
                  color="primary"
                  max={99}
                >
                  <DescriptionIcon
                    fontSize="small"
                    sx={{ color: "text.disabled" }}
                  />
                </Badge>
              </IconButton>
            )}
          </Box>
        }
      >
        {categories}
      </TreeItem>
    );
  };

  // A plant's WBS bucketed into one folder per discipline, labeled by type
  // code (PIE / PVE / STE / GA / Other), in WBS_GROUPS order.
  // Empty disciplines are dropped rather than shown as empty folders.
  const renderWbsGroups = (plant, visibleWbs) => {
    const byGroup = new Map();
    visibleWbs.forEach((wbs) => {
      const key = treeGroupKey(wbs);
      if (!byGroup.has(key)) byGroup.set(key, []);
      byGroup.get(key).push(wbs);
    });

    return WBS_GROUPS.filter((group) => byGroup.has(group.key)).map((group) => {
      const groupWbs = byGroup.get(group.key);
      const groupId = wbsGroupNodeId(plant.id, group.key);
      // Null for "Other" and for legacy-only disciplines like GA - there is
      // no current type to create them as, so those folders get no add button.
      const groupType = disciplineDefaultType(group.key);

      return (
        <TreeItem
          key={groupId}
          nodeId={groupId}
          icon={
            <AccountTreeIcon
              fontSize="inherit"
              sx={{ color: "text.disabled" }}
            />
          }
          label={
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography
                variant="caption"
                fontWeight={600}
                color="text.secondary"
              >
                {group.label}
              </Typography>
              <Typography variant="caption" color="text.disabled">
                ({groupWbs.length})
              </Typography>
              {/* No "add another" on a one-per-plant type: the folder only
                  shows once that one WBS exists, so a second can't be valid. */}
              {!pickerMode &&
                groupType &&
                !isSingletonProjectType(groupType) && (
                  <IconButton
                    size="small"
                    title={`Add another ${projectTypeLabel(groupType)} WBS`}
                    onClick={(event) => {
                      event.stopPropagation();
                      openAddWbs(plant, groupType);
                    }}
                    sx={{ p: 0.25 }}
                  >
                    <AddIcon fontSize="small" sx={{ color: "primary.main" }} />
                  </IconButton>
                )}
            </Box>
          }
        >
          {groupWbs.map((wbs) => renderWbsNode(plant, wbs))}
        </TreeItem>
      );
    });
  };

  // A plant's Software WBS (see isSoftwareWbs) bucketed into one folder per
  // stored type, alphabetically, with untyped ones last. Same folder look as
  // renderWbsGroups, minus the add button: these are legacy / unrecognised
  // types, which nothing creates any more.
  const renderSoftwareGroups = (plant, softwareWbs) => {
    const byGroup = new Map();
    softwareWbs.forEach((wbs) => {
      const key = softwareGroupKey(wbs);
      if (!byGroup.has(key)) byGroup.set(key, []);
      byGroup.get(key).push(wbs);
    });

    const keys = [...byGroup.keys()].sort((a, b) => {
      if (a === "none") return 1;
      if (b === "none") return -1;
      return a.localeCompare(b);
    });

    return keys.map((key) => {
      const groupWbs = byGroup.get(key);
      const groupId = softwareGroupNodeId(plant.id, key);

      return (
        <TreeItem
          key={groupId}
          nodeId={groupId}
          icon={
            <AccountTreeIcon
              fontSize="inherit"
              sx={{ color: "text.disabled" }}
            />
          }
          label={
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography
                variant="caption"
                fontWeight={600}
                color="text.secondary"
              >
                {softwareGroupLabel(key)}
              </Typography>
              <Typography variant="caption" color="text.disabled">
                ({groupWbs.length})
              </Typography>
            </Box>
          }
        >
          {groupWbs.map((wbs) => renderWbsNode(plant, wbs))}
        </TreeItem>
      );
    });
  };

  // Document Register + Add WBS buttons for a plant. Rendered either on the
  // plant's own row or, once the list is focused on that plant, up in the
  // header next to the breadcrumb - identical either way.
  const renderPlantActions = (plant) => {
    if (pickerMode) return null;
    const wbsList = wbsByPlant[plant.id];

    return (
      <>
        <IconButton
          size="small"
          title="Open Document Register"
          onClick={(event) => {
            event.stopPropagation();
            navigate(`/plant/${plant.id}/document-register`);
          }}
          sx={{ p: 0.4 }}
        >
          <LibraryBooksIcon fontSize="small" color="primary" />
        </IconButton>
        <IconButton
          size="small"
          title={
            Array.isArray(wbsList)
              ? `${wbsList.length} WBS — click to add another`
              : "Add a WBS to this plant"
          }
          onClick={(event) => {
            event.stopPropagation();
            openAddWbs(plant);
          }}
          sx={{ p: 0.4 }}
        >
          <Badge
            badgeContent={Array.isArray(wbsList) ? wbsList.length : undefined}
            color="primary"
            max={99}
          >
            <AccountTreeIcon fontSize="small" sx={{ color: "primary.main" }} />
          </Badge>
        </IconButton>
      </>
    );
  };

  const renderPlantNode = (plant) => {
    const id = plantNodeId(plant.id);
    nodeTargetRef.current[id] = { kind: "plant", plant };
    const wbsList = wbsByPlant[plant.id];

    const wbsChildren = [];
    const softwareChildren = [];
    if (wbsList === "loading") {
      wbsChildren.push(
        <TreeItem
          key={`${id}-loading`}
          nodeId={`${id}-loading`}
          label={
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5 }}
            >
              <CircularProgress size={12} />
              <Typography variant="caption" color="text.secondary">
                Loading WBS…
              </Typography>
            </Box>
          }
        />,
      );
    } else if (Array.isArray(wbsList)) {
      const visibleWbs = wbsList.filter((wbs) => isWbsVisible(plant, wbs));
      const projectWbs = visibleWbs.filter((wbs) => !isSoftwareWbs(wbs));
      const softwareWbs = visibleWbs.filter(isSoftwareWbs);
      if (softwareWbs.length > 0) {
        softwareChildren.push(renderSoftwareGroups(plant, softwareWbs));
      }
      if (projectWbs.length > 0) {
        wbsChildren.push(renderWbsGroups(plant, projectWbs));
      } else {
        wbsChildren.push(
          <TreeItem
            key={`${id}-empty`}
            nodeId={`${id}-empty`}
            label={
              <Typography variant="caption" color="text.secondary">
                {/* Visible WBS that are all Software WBS still means this
                    section has none - it isn't a failed search. */}
                {visibleWbs.length > 0 || wbsList.length === 0
                  ? "No WBS"
                  : "No matches"}
              </Typography>
            }
          />,
        );
      }
    } else {
      wbsChildren.push(
        <TreeItem
          key={`${id}-placeholder`}
          nodeId={`${id}-placeholder`}
          label=""
        />,
      );
    }

    const procurementChildren = PROCUREMENT_CATEGORIES.map((category) =>
      renderProcurementNode(plant, category),
    );

    return (
      <TreeItem
        key={id}
        nodeId={id}
        icon={<FolderIcon fontSize="inherit" sx={{ color: "primary.main" }} />}
        label={
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              minWidth: 0,
            }}
          >
            <Typography
              variant="body2"
              noWrap
              title={plant.name}
              sx={{
                flex: 1,
                minWidth: 0,
                fontWeight: plant.id === activePlantId ? 700 : 500,
              }}
            >
              {plant.name || "Untitled"}
            </Typography>
            {!focusSelectedPlant && renderPlantActions(plant)}
          </Box>
        }
      >
        <TreeItem
          key={`${id}-registers-section`}
          nodeId={`${id}-registers-section`}
          className="tree-section-label"
          label="Registers"
        />
        {REGISTER_TILES.map((item) => renderModuleItemNode(plant, item))}
        <TreeItem
          key={`${id}-wbs-section`}
          nodeId={`${id}-wbs-section`}
          className="tree-section-label tree-section-label--wbs"
          label="Projects"
        />
        {wbsChildren}
        {softwareChildren.length > 0 && (
          <>
            <TreeItem
              key={`${id}-software-section`}
              nodeId={`${id}-software-section`}
              className="tree-section-label tree-section-label--software"
              label="Software WBS's"
            />
            {softwareChildren}
          </>
        )}
        <TreeItem
          key={`${id}-procurement-section`}
          nodeId={`${id}-procurement-section`}
          className="tree-section-label tree-section-label--procurement"
          label="Procurement"
        />
        {procurementChildren}
        {EMS_MODULE_GROUP && (
          <>
            <TreeItem
              key={`${id}-ems-section`}
              nodeId={`${id}-ems-section`}
              className="tree-section-label"
              label="EMS"
            />
            {EMS_MODULE_GROUP.modules.map((item) =>
              renderModuleItemNode(plant, item),
            )}
          </>
        )}
        {CMS_MODULE_GROUP && (
          <>
            <TreeItem
              key={`${id}-cms-section`}
              nodeId={`${id}-cms-section`}
              className="tree-section-label"
              label="CMS"
            />
            {CMS_MODULE_GROUP.modules.map((item) =>
              renderModuleItemNode(plant, item),
            )}
          </>
        )}
        {SCHEDULE_MODULE_GROUP && (
          <>
            <TreeItem
              key={`${id}-schedule-section`}
              nodeId={`${id}-schedule-section`}
              className="tree-section-label"
              label="4D Schedule"
            />
            {SCHEDULE_MODULE_GROUP.modules.map((item) =>
              renderModuleItemNode(plant, item),
            )}
          </>
        )}
      </TreeItem>
    );
  };

  nodeTargetRef.current = {};
  const visiblePlants = plants
    .filter(isPlantVisible)
    .filter((plant) => !focusedPlant || plant.id === focusedPlant.id);

  // The plant whose actions the header offers. Falls back to the plant of the
  // page currently open, so the header keeps a target even before anything is
  // picked in the tree. In this mode the per-row buttons are dropped entirely
  // - repeating them down every plant row is what crowded the list.
  const headerPlant = focusSelectedPlant
    ? focusedPlant || plants.find((plant) => plant.id === activePlantId) || null
    : null;

  const confirmDialogTitle = confirmTarget
    ? confirmTarget.kind === "drawing"
      ? "Open drawing in MCS?"
      : confirmTarget.kind === "new-revision"
        ? "Start a new revision?"
        : `Open in ${confirmTarget.openLabel || confirmTarget.subTabLabel || confirmTarget.topTabLabel || "Estimation"}?`
    : "";

  const confirmDialogBody =
    confirmTarget?.kind === "drawing" ? (
      <>
        You're about to leave this page and open drawing{" "}
        <strong>{confirmTarget.dwgNumber || "Untitled drawing"}</strong>
        {confirmTarget.revision
          ? ` (Rev ${confirmTarget.revision})`
          : ""} from <strong>{confirmTarget.projectName}</strong> in MCS's BOM
        Dashboard. Continue?
      </>
    ) : confirmTarget?.kind === "new-revision" ? (
      <>
        You're about to leave this page and start a new revision
        {confirmTarget.revision
          ? ` (Rev ${confirmTarget.revision})`
          : ""} of <strong>{confirmTarget.projectName}</strong> in the{" "}
        {confirmTarget.subTabLabel || confirmTarget.topTabLabel} tab. Like the
        module's own "Add New Revision", it isn't stored until you save it there
        — and any unsaved changes here won't be saved. Continue?
      </>
    ) : confirmTarget ? (
      <>
        You're about to leave this page and open{" "}
        <strong>{confirmTarget.projectName}</strong>
        {/* Spec has no revisions of its own to name - see renderSpecNode. */}
        {confirmTarget.revision != null
          ? ` — revision ${confirmTarget.revision} — `
          : " "}
        in the{" "}
        {confirmTarget.openLabel ||
          confirmTarget.subTabLabel ||
          confirmTarget.topTabLabel}{" "}
        tab. Any unsaved changes here won't be saved. Continue?
      </>
    ) : null;

  return (
    <>
      <Box sx={{ px: 2, py: 1.5 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.25,
            minWidth: 0,
            mb: listOpen ? 1 : 0,
          }}
        >
          {collapsible ? (
            <ButtonBase
              // Doubles as a breadcrumb once a plant is picked: clicking
              // "Projects" then steps back out to the full list instead of
              // closing the panel's tree entirely.
              onClick={() => {
                if (!focusedPlant) {
                  setListOpen((v) => !v);
                  return;
                }
                // Back to the full list means back to a collapsed one: what
                // was left open inside the plant being stepped out of (or
                // opened by an earlier search) would otherwise still be
                // unfurled among all the other projects.
                setFocusedPlantId(null);
                setExpanded([]);
              }}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.25,
                borderRadius: 1,
                px: 0.5,
                ml: -0.5,
                py: 0.25,
                color: "text.secondary",
                "&:hover": { bgcolor: "action.hover", color: "text.primary" },
              }}
            >
              {listOpen ? (
                <ExpandMoreIcon fontSize="small" />
              ) : (
                <ChevronRightIcon fontSize="small" />
              )}
              <Typography variant="subtitle2" color="inherit">
                Projects
              </Typography>
            </ButtonBase>
          ) : (
            <Typography variant="subtitle2" color="text.secondary">
              Projects
            </Typography>
          )}
          {listOpen && focusedPlant && (
            <>
              <Typography variant="subtitle2" color="text.disabled">
                /
              </Typography>
              <Typography
                variant="subtitle2"
                noWrap
                title={focusedPlant.name}
                sx={{ minWidth: 0 }}
              >
                {focusedPlant.name}
              </Typography>
            </>
          )}
          {listOpen && headerPlant && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                flexShrink: 0,
                ml: "auto",
                pl: 1,
                pr: 0.75,
              }}
              // Named when it isn't obvious from a breadcrumb which plant
              // these act on.
              title={focusedPlant ? undefined : headerPlant.name}
            >
              {!pickerMode && (
                <PlantModuleNav
                  plantId={headerPlant.id}
                  groups={PLANT_MODULE_GROUPS}
                  treeData={data}
                  compact
                />
              )}
              {renderPlantActions(headerPlant)}
            </Box>
          )}
        </Box>
        {listOpen && (
          <TextField
            value={search}
            onChange={(e) => handleSearchInput(e.target.value)}
            placeholder="Search project, WBS, doc..."
            size="small"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="disabled" />
                </InputAdornment>
              ),
              endAdornment: isSearchLoading ? (
                <InputAdornment position="end">
                  <CircularProgress size={14} />
                </InputAdornment>
              ) : null,
            }}
          />
        )}
      </Box>
      <Divider />

      {showLegend && listOpen && (
        <>
          <Box
            sx={{
              px: 2,
              py: 0.75,
              display: "flex",
              flexWrap: "wrap",
              gap: 1.5,
            }}
          >
            {[
              { color: "primary.main", label: "Structure" },
              { color: "warning.main", label: "Opens elsewhere" },
              { color: "text.disabled", label: "Info only" },
            ].map(({ color, label }) => (
              <Box
                key={label}
                sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
              >
                <Box
                  sx={{
                    width: 14,
                    height: 0,
                    borderTop: "2px solid",
                    borderColor: color,
                  }}
                />
                <Typography variant="caption" color="text.secondary">
                  {label}
                </Typography>
              </Box>
            ))}
          </Box>
          <Divider />
        </>
      )}

      {!listOpen ? null : plantsLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress size={24} />
        </Box>
      ) : visiblePlants.length === 0 ? (
        <Box sx={{ px: 2, py: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {query ? "No matches." : "No projects available."}
          </Typography>
        </Box>
      ) : (
        <TreeView
          expanded={expanded}
          onNodeToggle={handleNodeToggle}
          onNodeSelect={handleNodeSelect}
          defaultCollapseIcon={<ExpandMoreIcon />}
          defaultExpandIcon={<ChevronRightIcon />}
          sx={(theme) => {
            const structuralLine = alpha(theme.palette.primary.main, 0.45);
            const inertLine = alpha(theme.palette.text.primary, 0.22);
            const accentLine = alpha(theme.palette.warning.main, 0.75);

            const trunk = (color, width = 2) => ({
              borderLeft: `${width}px solid ${color}`,
            });
            const stub = (color, width = 2) => ({
              content: '""',
              position: "absolute",
              top: "50%",
              left: "-14px",
              width: "14px",
              height: 0,
              borderTop: `${width}px solid ${color}`,
            });

            return {
              flexGrow: 1,
              overflowY: "auto",
              px: 1,
              py: 1,
              "& .MuiTreeItem-content": { py: 0.5, position: "relative" },
              "& .tree-section-label > .MuiTreeItem-content": {
                background: (theme) => alpha(theme.palette.text.disabled, 0.04),
                borderRadius: "6px",
                mt: 1,
                mb: 0.25,
                px: 1,
                py: "2px !important",
                cursor: "default",
                "&:hover": {
                  background: (theme) =>
                    alpha(theme.palette.text.disabled, 0.04),
                },
                "& .MuiTreeItem-label": {
                  fontSize: "10px",
                  fontWeight: 700,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: "text.disabled",
                  py: 0,
                },
              },
              "& .tree-section-label > .MuiTreeItem-content .MuiTreeItem-iconContainer":
                {
                  display: "none",
                },
              "& .tree-section-label--wbs > .MuiTreeItem-content .MuiTreeItem-iconContainer":
                {
                  display: "none",
                },
              "& .tree-section-label--procurement > .MuiTreeItem-content .MuiTreeItem-iconContainer":
                {
                  display: "none",
                },
              // Draw the tree as branches: a solid trunk running down the
              // left edge of every nested group, with a short stub reaching
              // out to each item's icon — colour-coded per branch so
              // Plant → WBS → category → item reads as a drawn, labeled
              // tree instead of plain indentation.
              "& .MuiTreeItem-group": {
                marginLeft: "15px",
                paddingLeft: "14px",
                ...trunk(structuralLine),
              },
              "& .MuiTreeItem-group > .MuiTreeItem-root > .MuiTreeItem-content::before":
                stub(structuralLine),
              "& .branch-inert > .MuiTreeItem-group": trunk(inertLine, 1.5),
              "& .branch-inert > .MuiTreeItem-group > .MuiTreeItem-root > .MuiTreeItem-content::before":
                stub(inertLine, 1.5),
              "& .branch-accent > .MuiTreeItem-group": trunk(accentLine),
              "& .branch-accent > .MuiTreeItem-group > .MuiTreeItem-root > .MuiTreeItem-content::before":
                stub(accentLine),
            };
          }}
        >
          {visiblePlants.map(renderPlantNode)}
        </TreeView>
      )}

      {!pickerMode && (
        <>
          <Dialog
            open={Boolean(confirmTarget)}
            onClose={handleConfirmCancel}
            maxWidth="xs"
            fullWidth
          >
            <DialogTitle>{confirmDialogTitle}</DialogTitle>
            <DialogContent>
              <DialogContentText sx={{ wordBreak: "break-word" }}>
                {confirmDialogBody}
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleConfirmCancel}>Cancel</Button>
              <Button onClick={handleConfirmOpen} variant="contained" autoFocus>
                {confirmTarget?.kind === "new-revision" ? "Create" : "Open"}
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog
            open={Boolean(addWbsTarget)}
            onClose={() => !savingWbs && setAddWbsTarget(null)}
            maxWidth="xs"
            fullWidth
          >
            <DialogTitle>
              Add {lockedWbsType ? `${lockedWbsType.toUpperCase()} ` : ""}WBS —{" "}
              {addWbsTarget?.name}
            </DialogTitle>
            <DialogContent>
              <Box
                sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}
              >
                <TextField
                  label="WBS Code"
                  value={wbsForm.projectName}
                  onChange={(e) =>
                    setWbsForm((prev) => ({
                      ...prev,
                      projectName: e.target.value,
                    }))
                  }
                  fullWidth
                  required
                  autoFocus
                />
                <TextField
                  label="Description (optional)"
                  value={wbsForm.description}
                  onChange={(e) =>
                    setWbsForm((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                  helperText="Not saved - shown here for reference only"
                  fullWidth
                  multiline
                  minRows={2}
                />
                <TextField
                  label="Type"
                  select
                  value={wbsForm.type}
                  onChange={(e) =>
                    setWbsForm((prev) => ({ ...prev, type: e.target.value }))
                  }
                  fullWidth
                  required
                  disabled={Boolean(lockedWbsType)}
                  helperText={
                    lockedWbsType
                      ? "Set by the folder this was added from"
                      : undefined
                  }
                >
                  {(lockedWbsType
                    ? PROJECT_TYPES.filter((t) => t.value === lockedWbsType)
                    : PROJECT_TYPES
                  ).map((t) => {
                    const taken = isSingletonTypeTaken(addWbsTarget, t.value);
                    return (
                      <MenuItem key={t.value} value={t.value} disabled={taken}>
                        {taken ? `${t.label} (only one per plant)` : t.label}
                      </MenuItem>
                    );
                  })}
                </TextField>
                <TextField
                  label="SharePoint Link (optional)"
                  value={wbsForm.link}
                  onChange={(e) =>
                    setWbsForm((prev) => ({ ...prev, link: e.target.value }))
                  }
                  fullWidth
                  type="url"
                />
                {wbsError && (
                  <Typography variant="caption" color="error">
                    {wbsError}
                  </Typography>
                )}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setAddWbsTarget(null)}
                disabled={savingWbs}
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateWbs}
                variant="contained"
                disabled={savingWbs}
              >
                {savingWbs ? "Creating…" : "Create"}
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog
            open={Boolean(addDocTarget)}
            onClose={() => !savingDoc && setAddDocTarget(null)}
            maxWidth="xs"
            fullWidth
          >
            <DialogTitle>
              Add Document — {addDocTarget && wbsLabel(addDocTarget.wbs)}
            </DialogTitle>
            <DialogContent>
              <Box
                sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}
              >
                <Autocomplete
                  options={documentAbbreviations}
                  value={docForm.abbrEntry}
                  onChange={(_e, value) =>
                    setDocForm((prev) => ({ ...prev, abbrEntry: value }))
                  }
                  getOptionLabel={(opt) =>
                    opt ? `${opt.description} (${opt.abbreviation})` : ""
                  }
                  isOptionEqualToValue={(opt, value) =>
                    opt?.abbreviation === value?.abbreviation
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Document Type"
                      required
                      autoFocus
                    />
                  )}
                />
                <TextField
                  label="DCS Type"
                  select
                  value={docForm.type}
                  onChange={(e) =>
                    setDocForm((prev) => ({ ...prev, type: e.target.value }))
                  }
                  fullWidth
                >
                  {DCS_DOC_TYPES.map((t) => (
                    <MenuItem key={t} value={t}>
                      {t}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  label="Description (optional)"
                  value={docForm.description}
                  placeholder={docForm.abbrEntry?.description || ""}
                  onChange={(e) =>
                    setDocForm((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                  fullWidth
                />
                <Box sx={{ display: "flex", gap: 2 }}>
                  <TextField
                    label="Revision (optional)"
                    value={docForm.revision}
                    onChange={(e) =>
                      setDocForm((prev) => ({
                        ...prev,
                        revision: e.target.value,
                      }))
                    }
                    fullWidth
                  />
                  <TextField
                    label="No. of Sheets (optional)"
                    type="number"
                    value={docForm.numberOfSheets}
                    onChange={(e) =>
                      setDocForm((prev) => ({
                        ...prev,
                        numberOfSheets: e.target.value,
                      }))
                    }
                    fullWidth
                  />
                </Box>
                {docError && (
                  <Typography variant="caption" color="error">
                    {docError}
                  </Typography>
                )}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setAddDocTarget(null)}
                disabled={savingDoc}
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateDoc}
                variant="contained"
                disabled={savingDoc}
              >
                {savingDoc ? "Creating…" : "Create"}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </>
  );
}
